import { FormState, Input, Icon, tokens, Button, Divider } from "@vintecc/components";
import { faUser, faLock, faSpinner, faKey } from "@vintecc/pro-light-svg-icons";
import { useState, useEffect } from "react";
import ENV from "../../env";
import { ExternalLink } from "../ExternalLink/ExternalLink";
import Logo from "../LogoComponent/LogoComponent";
import MsLogo from "../MsLogo/MsLogo";
import { LoginData, createLoginSchema } from "../../schemas/login.schema";
import { useAuthTranslation } from "../../services/culture/auth.translations";
import { useCommonTranslation } from "../../services/culture/common.translations";

export const LoginComponent = () => {
  const { t: tAuth } = useAuthTranslation();
  const { t: tCommon } = useCommonTranslation();
  const loginSchema = createLoginSchema(tAuth);
  const [step, setStep] = useState<"password" | "mfa">("password");
  const [error, setError] = useState<string | null>(null);

  // todo 26/05: preserver state from both forms here so mfa login works again
  const [formData, setFormData] = useState<LoginData>({
    UsernameOrEmail: "",
    Password: "",
    MfaCode: undefined,
  });
  const clientId = new URLSearchParams(window.location.search).get("client_id") || "00000000-0000-0000-0000-000000000000";
  const callbackUrl = new URLSearchParams(window.location.search).get("redirect_uri") || "";
  const response_type = new URLSearchParams(window.location.search).get("response_type") || "code";
  const scope = new URLSearchParams(window.location.search).get("scope") || "openid";
  const authState = new URLSearchParams(window.location.search).get("state") || "";
  const nonce = new URLSearchParams(window.location.search).get("nonce") || "";

  useEffect(() => {
    localStorage.setItem("client_id", clientId);
    localStorage.setItem("redirect_uri", callbackUrl);
    localStorage.setItem("response_type", response_type);
    localStorage.setItem("scope", scope);
    localStorage.setItem("state", authState);
    localStorage.setItem("nonce", nonce);
  }, [authState, callbackUrl, clientId, nonce, response_type, scope]);

  const submitLogin = async (data: LoginData) => {
    await fetch(
      `${ENV.API_SERVER}/login?client_id=${clientId}&redirect_uri=${callbackUrl}&response_type=${response_type}&scope=${scope}&state=${authState}&nonce=${nonce}`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      },
    )
      .then(async (response) => {
        switch (response.status) {
          case 200:
            return response.json();
          case 403:
            window.location.href = `${ENV.BASE_PATH}/migrate?client_id=${clientId}&callback_url=${callbackUrl}&response_type=${response_type}&scope=${scope}&state=${authState}&nonce=${nonce}`;
            break;
          default:
            setError(await response.text());
        }
      })
      .then((data?) => {
        if (!data) return;

        if (step === "password" && data.mfaRequired == true) {
          setStep("mfa");
          return;
        }
        if (data.redirectUri) {
          window.location.replace(data.redirectUri);
        }
      })
      .catch((error) => {
        setError(error.message);
      });
  };

  return (
    <div
      id="login"
      className="flex relative sm:items-center items-start justify-center xl:justify-end overflow-auto xl:px-64 border-solid border-10 xl:border-20 border-white p-8"
    >
      {/* Password step */}
      {step === "password" && (
        <>
          <FormState
            className="border-2 z-20 border-white p-12 sm:p-24 w-64 min-w-64 box-content space-y-8"
            defaultValue={formData}
            onSubmit={async (data) => {
              setFormData(data);
              submitLogin(data);
            }}
            validationSchema={loginSchema}
          >
            {({ state, setState, errors, isSubmitting }) => (
              <>
                <Logo />

                <div className="space-y-4">
                  <>
                    <Input
                      label={tAuth("login.usernameOrEmail")}
                      name="username"
                      testId="login-username-input"
                      value={state.UsernameOrEmail}
                      autoComplete="username"
                      onChange={(e) => setState({ ...state, UsernameOrEmail: e.target.value })}
                      helpText={errors.UsernameOrEmail}
                      helpTextIntent="danger"
                      startIcon={<Icon icon={faUser} color={tokens.color.grey[400]} />}
                    />

                    <Input
                      label={tAuth("login.password")}
                      name="password"
                      testId="login-password-input"
                      type="password"
                      value={state.Password}
                      autoComplete="current-password"
                      onChange={(e) => setState({ ...state, Password: e.target.value })}
                      helpText={errors.Password}
                      helpTextIntent="danger"
                      startIcon={<Icon icon={faLock} color={tokens.color.grey[400]} />}
                    />
                  </>

                  <p className="text-red-600" data-testid="login-error-message">
                    {error}
                  </p>
                  <div className="pt-2">
                    <Button
                      type="submit"
                      fullWidth
                      className="login-btn"
                      variant="filled"
                      intent="default"
                      testId="login-submit-button"
                      startIcon={isSubmitting ? <Icon icon={faSpinner} spin /> : undefined}
                      disabled={isSubmitting}
                    >
                      {tCommon("common.signIn")}
                    </Button>
                  </div>
                </div>

                <Divider label={tAuth("common.or")} />

                <Button
                  type="button"
                  fullWidth
                  className="ms-btn"
                  testId="login-microsoft-button"
                  startIcon={<MsLogo />}
                  disabled={isSubmitting}
                  onClick={() => {
                    window.location.replace(
                      `${ENV.API_SERVER}/microsoft/signin?client_id=${clientId}&redirect_uri=${callbackUrl}&response_type=${response_type}&scope=${scope}&state=${authState}&nonce=${nonce}`,
                    );
                  }}
                >
                  {tCommon("common.signInWithMicrosoft")}
                </Button>

                {(ENV.DISCLOSURE_URL || ENV.PRIVACY_POLICY_URL || ENV.TERMS_AND_CONDITIONS_URL) && (
                  <div className="flex justify-center items-center gap-2 pt-4 -mx-8">
                    {ENV.DISCLOSURE_URL && <ExternalLink url={ENV.DISCLOSURE_URL} label={tCommon("common.responsibleDisclosure")} />}
                    {ENV.PRIVACY_POLICY_URL && <ExternalLink url={ENV.PRIVACY_POLICY_URL} label={tCommon("common.privacyPolicy")} />}
                    {ENV.TERMS_AND_CONDITIONS_URL && <ExternalLink url={ENV.TERMS_AND_CONDITIONS_URL} label={tCommon("common.termsOfUse")} />}
                  </div>
                )}
              </>
            )}
          </FormState>
        </>
      )}

      {/* MFA step */}
      {step === "mfa" && (
        <>
          <FormState
            className="border-2 z-20 border-white p-12 sm:p-24 w-64 min-w-64 box-content space-y-8"
            defaultValue={formData}
            onSubmit={(data) => submitLogin({ ...formData, MfaCode: data.MfaCode })}
            validationSchema={loginSchema}
          >
            {({ state, setState, errors, isSubmitting }) => (
              <>
                <Logo />

                <div className="space-y-4">
                  <Input
                    label={tAuth("login.mfaCode")}
                    name="mfaCode"
                    testId="login-mfa-code-input"
                    value={state.MfaCode}
                    autoComplete="one-time-code"
                    onChange={(e) => setState({ ...state, MfaCode: e.target.value })}
                    helpText={errors.MfaCode}
                    helpTextIntent="danger"
                    startIcon={<Icon icon={faKey} color={tokens.color.grey[400]} />}
                  />

                  <p className="text-red-600" data-testid="login-error-message">
                    {error}
                  </p>
                  <div className="pt-2">
                    <Button
                      type="submit"
                      fullWidth
                      className="login-btn"
                      variant="filled"
                      intent="default"
                      testId="login-submit-button"
                      startIcon={isSubmitting ? <Icon icon={faSpinner} spin /> : undefined}
                      disabled={isSubmitting}
                    >
                      {tCommon("common.signIn")}
                    </Button>
                  </div>
                </div>

                <Divider label={tAuth("common.or")} />

                <Button
                  type="button"
                  fullWidth
                  className="ms-btn"
                  testId="login-microsoft-button"
                  startIcon={<MsLogo />}
                  disabled={isSubmitting}
                  onClick={() => {
                    window.location.replace(
                      `${ENV.API_SERVER}/microsoft/signin?client_id=${clientId}&redirect_uri=${callbackUrl}&response_type=${response_type}&scope=${scope}&state=${authState}&nonce=${nonce}`,
                    );
                  }}
                >
                  {tCommon("common.signInWithMicrosoft")}
                </Button>

                {(ENV.DISCLOSURE_URL || ENV.PRIVACY_POLICY_URL || ENV.TERMS_AND_CONDITIONS_URL) && (
                  <div className="flex justify-center items-center gap-2 pt-4 -mx-8">
                    {ENV.DISCLOSURE_URL && <ExternalLink url={ENV.DISCLOSURE_URL} label={tCommon("common.responsibleDisclosure")} />}
                    {ENV.PRIVACY_POLICY_URL && <ExternalLink url={ENV.PRIVACY_POLICY_URL} label={tCommon("common.privacyPolicy")} />}
                    {ENV.TERMS_AND_CONDITIONS_URL && <ExternalLink url={ENV.TERMS_AND_CONDITIONS_URL} label={tCommon("common.termsOfUse")} />}
                  </div>
                )}
              </>
            )}
          </FormState>
        </>
      )}
    </div>
  );
};
